import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Box } from '@mui/material';

import { useGetApiV5FilesIdNamingSuggestions } from 'api/v5/files/files';
import { useTrackEvent } from 'analytics/useTrackEvent';

import { COLORS } from 'components/AI/DeterministicFileNameManagement/constants';

import { FILE_RENAME_SUGGESTION_TARGET_SELECTOR } from './constants';
import { FileRenameCoachmarkModal } from './FileRenameCoachmarkModal';
import { getFoundInDocumentLabels } from './getFoundInDocumentLabels';
import { useFlyToTargetAnimation } from './useFlyToTargetAnimation';

export interface FileRenameCoachmarkProps {
  fileUuid: string;
  originalName: string;
  suggestedName: string;
  onDismiss: () => void;
  onSpotlight: () => void;
  targetSelector?: string;
}

export const FileRenameCoachmark = ({
  fileUuid,
  originalName,
  suggestedName,
  onDismiss,
  onSpotlight,
  targetSelector = FILE_RENAME_SUGGESTION_TARGET_SELECTOR,
}: FileRenameCoachmarkProps) => {
  const trackEvent = useTrackEvent();
  const trackFiredRef = useRef(false);

  useEffect(() => {
    if (trackFiredRef.current) return;
    trackFiredRef.current = true;
    trackEvent('rename_coachmark_shown', {
      source: 'onboarding',
      onboarding_step: 'rename_coachmark_shown',
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const suggestedNameRef = useRef<HTMLDivElement>(null);
  const [modalOpen, setModalOpen] = useState(true);
  const [showBackdrop, setShowBackdrop] = useState(true);
  const { ghost, flyToTarget } = useFlyToTargetAnimation();

  const { data: namingData } = useGetApiV5FilesIdNamingSuggestions(fileUuid, {
    query: { enabled: !!fileUuid },
  });

  const foundInDocumentLabels = getFoundInDocumentLabels(
    namingData?.preview_values ?? null,
  );

  const handleDismiss = useCallback(() => {
    setModalOpen(false);
    onDismiss();
  }, [onDismiss]);

  const handleShowWhereToAccept = useCallback(() => {
    const sourceEl = suggestedNameRef.current;
    if (!sourceEl) {
      setModalOpen(false);
      onSpotlight();
      return;
    }

    const sourceRect = sourceEl.getBoundingClientRect();
    setShowBackdrop(false);
    setModalOpen(false);

    flyToTarget(sourceRect, targetSelector, suggestedName, onSpotlight);
  }, [flyToTarget, onSpotlight, suggestedName, targetSelector]);

  return createPortal(
    <>
      <FileRenameCoachmarkModal
        open={modalOpen}
        originalName={originalName}
        suggestedName={suggestedName}
        foundInDocumentLabels={foundInDocumentLabels}
        onMaybeLater={handleDismiss}
        onShowWhereToAccept={handleShowWhereToAccept}
        showBackdrop={showBackdrop}
        ref={suggestedNameRef}
      />
      {ghost && (
        <Box
          aria-hidden
          sx={{
            position: 'fixed',
            zIndex: 1400,
            top: ghost.top,
            left: ghost.left,
            width: ghost.width,
            height: ghost.height,
            opacity: ghost.opacity,
            transition: ghost.transition,
            pointerEvents: 'none',
            px: 2.5,
            py: 2,
            borderRadius: 1.5,
            border: `2px solid ${COLORS.suggestion.cardBorder}`,
            bgcolor: COLORS.suggestion.cardBg,
            boxShadow: '0 8px 24px rgba(25, 118, 210, 0.2)',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          <Box
            component="span"
            sx={{
              fontWeight: 700,
              fontSize: '1.0625rem',
              wordBreak: 'break-word',
              color: COLORS.text.primary,
            }}
          >
            {ghost.label}
          </Box>
        </Box>
      )}
    </>,
    document.body,
  );
};
